import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Card from '@/components/base/Card';
import EmptyState from '@/components/base/EmptyState';
import ErrorState from '@/components/base/ErrorState';
import Skeleton from '@/components/base/Skeleton';
import Tabs, { type TabItem } from '@/components/base/Tabs';
import { useListScrollMemory } from '@/hooks/useListScrollMemory';
import { systemSettings } from '@/mocks/settings';
import { fetchOrders } from '@/services/ordersService';
import type { OrderStatus, OrderView } from '@/types';
import OrdersTable from './components/OrdersTable';

type TabId = 'new' | 'active' | 'completed' | 'cancelled';

const TAB_IDS: TabId[] = ['new', 'active', 'completed', 'cancelled'];

const ACTIVE_STATUSES: OrderStatus[] = [
  'accepted',
  'under_review',
  'in_progress',
  'ready_for_delivery',
];

function orderInTab(order: OrderView, tab: TabId): boolean {
  switch (tab) {
    case 'new':
      return order.status === 'new';
    case 'active':
      return ACTIVE_STATUSES.includes(order.status);
    case 'completed':
      return order.status === 'completed';
    case 'cancelled':
      return order.status === 'cancelled';
    default:
      return false;
  }
}

export default function OrdersPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const requested = searchParams.get('tab') as TabId | null;
  const tab: TabId = requested && TAB_IDS.includes(requested) ? requested : 'new';

  const [orders, setOrders] = useState<OrderView[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await fetchOrders();
      setOrders(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Returning from an order detail restores the previous scroll position of the
  // same tab once the rows are rendered again.
  useListScrollMemory(`orders:${tab}`, !loading);

  const rows = useMemo(
    () => (orders ? orders.filter((order) => orderInTab(order, tab)) : []),
    [orders, tab],
  );

  const tabs: TabItem[] = TAB_IDS.map((id) => ({
    id,
    label: t(`orders.tab.${id}`),
    count: orders ? orders.filter((order) => orderInTab(order, id)).length : undefined,
  }));

  const changeTab = (id: string) => {
    setSearchParams(id === 'new' ? {} : { tab: id }, { replace: true });
  };

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-lg font-bold text-foreground-950 md:text-xl">{t('orders.title')}</h1>
        <p className="mt-1 text-sm text-foreground-500">{t('orders.subtitle')}</p>
      </header>

      <Card padded={false} className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-200 px-4 py-3.5 md:px-5">
          <Tabs items={tabs} active={tab} onChange={changeTab} />
        </div>

        {loading ? (
          <div className="space-y-3 p-4 md:p-5">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-5 w-20" />
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-5 flex-1" />
                <Skeleton className="h-5 w-24" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="p-4 md:p-5">
            <ErrorState
              title={t('orders.error')}
              description={t('common.errorText')}
              retryLabel={t('common.retry')}
              onRetry={load}
            />
          </div>
        ) : rows.length === 0 ? (
          <div className="p-4 md:p-5">
            <EmptyState icon="ri-inbox-archive-line" title={t(`orders.empty.${tab}`)} />
          </div>
        ) : (
          <OrdersTable orders={rows} currency={systemSettings.currency} returnTab={tab} />
        )}

        {!loading && !error && rows.length > 0 ? (
          <div className="border-t border-background-100 bg-background-100/60 px-4 py-3 md:px-5">
            <p className="flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
              <i className="ri-information-line mt-0.5 text-base text-foreground-400" aria-hidden="true" />
              {t('orders.demoNote')}
            </p>
          </div>
        ) : null}
      </Card>
    </div>
  );
}